import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addApprover,
  cancelApproval,
  decideApproval,
  fetchAdjustment,
  fetchAdjustmentRaisers,
  fetchAdjustments,
  fetchApprovalQueue,
  fetchApprovers,
  fetchRaisableReasons,
  raiseAdjustment,
  removeApprover,
} from "@/api/adjustments";
import type { AdjustmentListQuery, RaiseAdjustmentPayload } from "@/interfaces/adjustments";

/**
 * Admin adjustments and the approval queue.
 *
 * Every mutation invalidates BOTH keys: a decision resolves a queue item and changes the
 * adjustment's status in the same breath, and a maker watching the blotter should see it move
 * without a reload.
 */

export function useAdjustments(query: AdjustmentListQuery = {}) {
  return useQuery({
    queryKey: ["adjustments", query],
    queryFn: () => fetchAdjustments(query),
  });
}

export function useAdjustment(id: number | null) {
  return useQuery({
    queryKey: ["adjustment", id],
    queryFn: () => fetchAdjustment(id as number),
    enabled: id != null,
  });
}

/** The "raised by" filter's options — only people who have actually raised one. */
export function useAdjustmentRaisers() {
  return useQuery({ queryKey: ["adjustmentRaisers"], queryFn: fetchAdjustmentRaisers });
}

/** The reason picker's options. Static for a session — the catalogue does not change under us. */
export function useRaisableReasons() {
  return useQuery({
    queryKey: ["adjustmentReasons"],
    queryFn: fetchRaisableReasons,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useApprovalQueue() {
  return useQuery({
    queryKey: ["approvalQueue"],
    queryFn: fetchApprovalQueue,
  });
}

function useQueueInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["adjustments"] });
    qc.invalidateQueries({ queryKey: ["approvalQueue"] });
  };
}

export function useRaiseAdjustment() {
  const invalidate = useQueueInvalidation();
  return useMutation({
    mutationFn: (payload: RaiseAdjustmentPayload) => raiseAdjustment(payload),
    onSuccess: invalidate,
  });
}

export function useDecideApproval() {
  const invalidate = useQueueInvalidation();
  return useMutation({
    mutationFn: (v: { id: number; approve: boolean; note?: string }) =>
      decideApproval(v.id, v.approve, v.note),
    onSuccess: invalidate,
  });
}

export function useCancelApproval() {
  const invalidate = useQueueInvalidation();
  return useMutation({
    mutationFn: (id: number) => cancelApproval(id),
    onSuccess: invalidate,
  });
}

/** The checker pool. Changing it changes who can act on the queue, so both keys are refreshed. */
export function useApprovers() {
  return useQuery({ queryKey: ["approvers"], queryFn: fetchApprovers });
}

function usePoolInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["approvers"] });
    qc.invalidateQueries({ queryKey: ["approvalQueue"] });
  };
}

export function useAddApprover() {
  const invalidate = usePoolInvalidation();
  return useMutation({
    mutationFn: (userId: number) => addApprover(userId),
    onSuccess: invalidate,
  });
}

export function useRemoveApprover() {
  const invalidate = usePoolInvalidation();
  return useMutation({
    mutationFn: (userId: number) => removeApprover(userId),
    onSuccess: invalidate,
  });
}
